import type { DateTime } from "luxon";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { useChosenDepartment } from "@/contexts/ChosenDepartment";
import { useErrorSnackbar } from "@/hooks/useErrorSnackbar";
import { useRosterLookup } from "@/hooks/useRosterLookup";
import { useFetchScheduleWeeks } from "@/hooks/useScheduleWeeks";
import { backendService } from "@/services/backend.service";
import { buildCostPrediction, PREDICT_COST_FAILED_EVENT } from "../costPrediction";
import { getShiftDisplayDate } from "../dateUtils";
import type { WeekData } from "../models";
import { dayIndexOfDate, weekIndexOfDate } from "../panelWeeks";
import { toIsoDate } from "../scheduleWeeks";
import { countDailyHours } from "../statsCalculations";
import { getShiftIndexByShiftTimes, unionForNurseId } from "../utilFunctions";
import { useStaffingTargetCheck } from "./useStaffingTargetCheck";
import { type EmployeeStatusWarningProps, useNurseWarnings } from "./WarningDialog";

type RunChecksParams = {
	nurseId: number;
	startTime: DateTime;
	endTime: DateTime;
	isGlobalSalary?: boolean;
	isAgency: boolean;
	hasPto: boolean;
	// Location the nurse is being added to. Required to gate on the staffing target; omit to skip
	// the target warning (e.g. flows with no location).
	locationName?: string | null;
	isActual?: boolean;
	// Edit flows: id of the shift being replaced. When set, the original shift is excluded from
	// both the predictCost weekly delta and the local daily-hours sum so it isn't double-counted
	// against the new times. Add flows leave this undefined.
	originalShiftId?: string;
	// On Leave / Suspended gate for the nurse on the shift date. Undefined when schedulable — no warning.
	employeeStatusWarning?: EmployeeStatusWarningProps;
};

/**
 * Wraps the shift cost prediction + nurse warning cascade used when adding or editing a shift.
 * Both flows share predictCost → compute OT/daily-hours → checkWarnings; this hook is the single
 * owner. Warnings are advisories — the cascade always resolves (confirm proceeds, cancel aborts).
 */
export const useShiftPredictionWarnings = (onConfirmed: () => void) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const { roster } = useRosterLookup(chosenDepartment.department_id);
	const fetchScheduleWeeks = useFetchScheduleWeeks();
	const showErrorSnackbar = useErrorSnackbar();
	const { trackError } = useAnalytics();
	const getTargetCheck = useStaffingTargetCheck();
	const [
		warningState,
		checkWarnings,
		handleAgencyConfirm,
		handleOvertimeConfirm,
		handleDailyHoursConfirm,
		handleCancelWarnings,
		handlePtoConfirm,
		handleTargetConfirm,
		handleEmployeeStatusConfirm,
	] = useNurseWarnings(onConfirmed, chosenDepartment.longShiftDuration);

	const runChecks = useCallback(
		async ({
			nurseId,
			startTime,
			endTime,
			isGlobalSalary,
			isAgency,
			hasPto,
			locationName,
			isActual,
			originalShiftId,
			employeeStatusWarning,
		}: RunChecksParams) => {
			// The schedule day the shift starts on, as the old list's by-date lookups resolved it.
			const scheduleDate = getShiftDisplayDate(startTime, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes).startOf("day");
			let weeks: WeekData[];
			try {
				weeks = await fetchScheduleWeeks(scheduleDate);
			} catch (error) {
				// Without the day's other shifts the daily-hours and staffing-target warnings can't be checked, so stop here.
				showErrorSnackbar(t("staffing.changes.schedule.load.failed"), { nurse_id: nurseId, date: toIsoDate(scheduleDate) }, error);
				return;
			}
			const weekIndex = weekIndexOfDate(weeks, scheduleDate);
			const dayIndex = dayIndexOfDate(weeks, scheduleDate);
			let dailyHours = 0;
			// Unified cost + OT through the shared builder instead of a local copy of the delta/format rules.
			// Seeded with the no-prediction result so a failed fetch shows "-" (unknown) rather than the old
			// formatCurrency(0) "$0", which read as a genuine zero-cost shift.
			let cost = buildCostPrediction(undefined, { isGlobalSalary: Boolean(isGlobalSalary) });

			try {
				const { data: prediction } = await backendService.predictCost({
					departmentId: chosenDepartment.department_id,
					nurseId,
					startTime: startTime.toISO() ?? "",
					endTime: endTime.toISO() ?? "",
					excludedShiftIds: originalShiftId ? [originalShiftId] : undefined,
				});
				cost = buildCostPrediction(prediction, { isGlobalSalary: Boolean(isGlobalSalary) });
				const shiftHours = prediction.weeklyHoursAfter - prediction.weeklyHoursBefore;
				const { weeklyShifts = [] } = weeks[weekIndex] || {};
				const originalDailyHours = countDailyHours({
					nurseId,
					departmentId: chosenDepartment.department_id,
					weeklyShifts,
					dayIndex,
					breakRulesConfig: chosenDepartment.breakRulesConfig ?? [],
					excludedShiftId: originalShiftId,
					nurseUnion: unionForNurseId(roster, nurseId),
				});
				dailyHours = originalDailyHours + shiftHours;
			} catch (error) {
				// Backend unavailable — fall back to PTO/agency-only warnings, and report it: the OT and daily-hours
				// warnings are otherwise lost with nothing to alert on (the console line only helps a developer).
				console.error("predictCost failed; skipping OT/daily-hours warnings", error);
				trackError(PREDICT_COST_FAILED_EVENT, {
					source: "shift_prediction_warnings",
					nurse_id: nurseId,
					department_id: chosenDepartment.department_id,
					error_message: error instanceof Error ? error.message : String(error),
				});
			}

			// Editing a shift doesn't add a body to the location, so only gate the target on adds.
			const targetWarning =
				!originalShiftId && locationName
					? getTargetCheck({
							weeks,
							locationName,
							weekIndex,
							dayNo: dayIndex,
							// The department's shift type for the start hour, as before; its row is then found by that type.
							shiftIndex:
								weeks[weekIndex]?.weeklyShifts.findIndex(
									(row) => row.type === chosenDepartment.shift_types[getShiftIndexByShiftTimes({ startTime, endTime })],
								) ?? -1,
							isActual: !!isActual,
						})
					: undefined;

			checkWarnings(isAgency, cost.overtimeHours ?? 0, cost.costDisplay, dailyHours, isActual ?? false, hasPto, targetWarning, employeeStatusWarning);
		},
		[chosenDepartment, roster, fetchScheduleWeeks, showErrorSnackbar, t, checkWarnings, getTargetCheck, trackError],
	);

	return {
		warningState,
		runChecks,
		handleAgencyConfirm,
		handleOvertimeConfirm,
		handleDailyHoursConfirm,
		handleCancelWarnings,
		handlePtoConfirm,
		handleTargetConfirm,
		handleEmployeeStatusConfirm,
		longShiftDuration: chosenDepartment.longShiftDuration,
	};
};
